// ── Dynamic Interaction Diagrams — built from the live "New Customer" config ──
// Unlike interactionDiagrams.ts (fixed reference diagrams shown for Existing
// Customer, keyed only by platform version), these are generated on every
// render from the actual configuration a rep has set in each product's
// config panel, so the diagram always reflects what's really configured.
import type {
  IdvFlowConfig, IdvProvider, IdvSmsConfig, IdvVendorSettings,
  CascadeCheckConfig, AmlScreeningConfig, FraudSignalConfig, TmRuleConfig,
  KybCheckConfig, KybOwnershipConfig, DuplicateRuleConfig,
} from './types'

type PlatformVersion = 'v1' | 'v2'

// ── IDV & Biometrics ───────────────────────────────────────────────────────

export interface IdvDiagramParams {
  platformVersion: PlatformVersion
  flow: IdvFlowConfig
  vendorSettings: Record<IdvProvider, IdvVendorSettings>
  defaultProvider: IdvProvider
  smsConfig: IdvSmsConfig
  autoRunKyc: boolean
  cleanupUrlsOnComplete: boolean
}

export function buildIdvDiagramDsl(p: IdvDiagramParams): string {
  const vendorName = p.flow.provider ?? p.defaultProvider
  const vendor = p.vendorSettings[vendorName]
  const isV1 = p.platformVersion === "v1"
  const lines: string[] = []

  lines.push(`title IDV & Biometrics — ${p.flow.name}${isV1 ? " (V1)" : ""} — Interaction Flow`, "")
  lines.push('participant endUser "Applicant" "Completes capture"')
  lines.push('participant clientApp "Client App" "Your product UI"')
  lines.push(`participant frankieOne "FrankieOne" "${isV1 ? "V1 Hosted + Compliance API" : "Platform"}"`)
  lines.push(`participant vendor "Biometrics Vendor" "${vendorName}"`, "")

  if (isV1) {
    lines.push("clientApp->frankieOne: Generate hosted onboarding URL")
    lines.push("  POST /idv/v2/idvalidate/onboarding-url")
    lines.push(`  >{"consent":true,"flowId":"${p.flow.flowId}","customerRef":"cust-123"}`)
    lines.push('  <{"entityId":"BF-abc123...","url":"https://hosted.uat.frankiefinancial.io/idv?token=..."}', "")
  } else {
    lines.push("clientApp->frankieOne: Generate Hosted OneSDK URL")
    lines.push("  POST /v2/individuals/hostedUrl")
    lines.push(`  >{"consentObtained":true,"oneSDKFlowId":"${p.flow.flowId}","redirectUrl":"https://your-app.com/complete"}`)
    lines.push('  <{"entityId":"BF-abc123...","url":"https://hosted.uat.frankie.one/idv?token=..."}', "")
  }

  lines.push("frankieOne-->clientApp: Return hosted URL + entityId", "")

  if (p.smsConfig.sendSms) {
    lines.push(`frankieOne->>endUser: Send SMS with verification link (sender: ${p.smsConfig.senderId})`, "")
  }

  lines.push("clientApp->endUser: Embed hosted URL in iframe", "")
  lines.push("endUser->frankieOne: Opens hosted session, grants consent", "")

  if (vendor?.mockMode) {
    lines.push(`note frankieOne: ${vendorName} connector is in Mock Mode — results below are simulated, not sent to the real ${vendorName} service`, "")
  }

  lines.push("self frankieOne: Render document capture screen", "")
  lines.push("endUser->frankieOne: Photographs ID document (front + back)", "")
  lines.push("frankieOne->vendor: Extract document data (OCR) + check document authenticity", "")
  lines.push("vendor-->frankieOne: Document data + authenticity result", "")
  lines.push("self frankieOne: Render face scan / liveness screen", "")
  lines.push("endUser->frankieOne: Completes selfie + liveness check", "")
  lines.push("frankieOne->vendor: Liveness detection + face match against document photo", "")
  lines.push("vendor-->frankieOne: Liveness result + similarity score", "")
  lines.push(`self frankieOne: Combine document + biometric results${isV1 ? "" : " into workflow outcome"}`, "")
  lines.push("frankieOne->endUser: Show verification result on hosted page", "")

  if (p.cleanupUrlsOnComplete) {
    lines.push("note frankieOne: Hosted URL is invalidated immediately so it can't be reused", "")
  }

  if (isV1) {
    lines.push("frankieOne->>clientApp: Webhook — UpdateCheckEntity (functionResult: COMPLETED)")
    lines.push("  POST https://your-server.com/webhook/frankieone", "")
    lines.push("note clientApp: Wait for this webhook before fetching results — calling too early risks a race condition on V1", "")
    lines.push("clientApp->frankieOne: Fetch verification result")
    lines.push("  GET /entity/{entityId}/checks", "")
    lines.push("frankieOne-->clientApp: Return checks summary + actionRecommended (PASS/FAIL/REFER)")
  } else {
    lines.push("frankieOne->>clientApp: Webhook — IDV workflow complete")
    lines.push("  POST https://your-server.com/webhook/frankieone")
    lines.push('  >{"entityId":"BF-abc123...","workflowExecutionId":"bf-wf-xxxx","status":"COMPLETE"}', "")
    lines.push("clientApp->frankieOne: Fetch full result")
    lines.push("  GET /v2/individuals/{entityId}", "")
    lines.push("frankieOne-->clientApp: Return biometrics + document verification result")
  }

  lines.push("")
  lines.push(p.autoRunKyc
    ? "frankieOne->>clientApp: Automatically trigger KYC verification for this entity"
    : "note clientApp: Automatic KYC trigger is off — verification must be started manually")

  return lines.join("\n")
}

// ── KYC + Cascade ──────────────────────────────────────────────────────────

export interface KycDiagramParams {
  platformVersion: PlatformVersion
  workflowName: string
  checks: CascadeCheckConfig[]
}

export function buildKycDiagramDsl(p: KycDiagramParams): string {
  const isV1 = p.platformVersion === "v1"
  const enabled = p.checks.filter((c) => c.enabled)
  const lines: string[] = []

  lines.push(`title KYC + Cascade${isV1 ? " (V1)" : ""} — Interaction Flow`, "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push(`participant frankieOne "FrankieOne" "${isV1 ? "V1 Compliance API" : "Platform"}"`)
  lines.push('participant sources "Cascade Sources" "DVS, Experian, Equifax..."', "")

  if (isV1) {
    lines.push("clientApp->frankieOne: Create + verify entity in one synchronous call")
    lines.push("  POST /entity/new/verify/profile/simple")
    lines.push('  >{"entity":{"entityProfile":"safe_harbour","entityType":"INDIVIDUAL"}}', "")
    lines.push("self frankieOne: Start verification cascade for this entity profile", "")
  } else {
    lines.push("clientApp->frankieOne: Create individual + execute workflow")
    lines.push(`  POST /v2/individuals/new/serviceprofiles/DEFAULT/workflows/${p.workflowName || "{workflow}"}/execute`, "")
    lines.push("self frankieOne: Start verification cascade for this workflow", "")
  }

  if (enabled.length === 0) {
    lines.push("note frankieOne: No cascade checks are currently enabled — every applicant would be referred for manual review", "")
  } else {
    lines.push("group Cascade — waterfall, stops at first PASS")
    enabled.forEach((check, i) => {
      if (check.id === "manual-review") {
        lines.push(`  self frankieOne: ${check.label} — route anything inconclusive to a human reviewer`)
      } else {
        lines.push(`  frankieOne->sources: Step ${i + 1} — ${check.label}`)
        lines.push("  sources-->frankieOne: Result")
      }
    })
    lines.push("end", "")
  }

  lines.push("self frankieOne: Apply ruleset — decide PASS / FAIL / REFER", "")

  if (isV1) {
    lines.push("frankieOne-->clientApp: Return result synchronously — no polling needed")
    lines.push('  <{"entityId":"BF-abc123...","entityProfileResult":{"actionRecommended":"PASS"}}')
  } else {
    lines.push("frankieOne-->clientApp: Return workflow result synchronously")
    lines.push('  <{"workflowResult":{"workflowExecutionState":"COMPLETED","individualProfileResult":"PASS"}}')
  }
  lines.push("", "self clientApp: Store result, update application status")

  return lines.join("\n")
}

// ── AML ────────────────────────────────────────────────────────────────────

export interface AmlDiagramParams {
  platformVersion: PlatformVersion
  config: AmlScreeningConfig
}

export function buildAmlDiagramDsl(p: AmlDiagramParams): string {
  const isV1 = p.platformVersion === "v1"
  const lines: string[] = []

  lines.push(`title AML${isV1 ? " (V1)" : ""} — Interaction Flow`, "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push(`participant frankieOne "FrankieOne" "${isV1 ? "V1 Compliance API" : "Platform"}"`)
  lines.push('participant amlVendor "AML Vendor" "ComplyAdvantage / Refinitiv"', "")

  lines.push(`note frankieOne: AML runs as part of the same ${isV1 ? "entity verify call" : "KYC workflow execution"} — no separate call needed`, "")

  if (p.config.pep) {
    lines.push("frankieOne->amlVendor: Screen individual — PEP (Politically Exposed Person) lists", "")
    lines.push("amlVendor-->frankieOne: PEP result", "")
  }
  if (p.config.sanctions) {
    lines.push("frankieOne->amlVendor: Screen individual — Sanctions lists", "")
    lines.push("amlVendor-->frankieOne: Sanctions result", "")
  }
  if (p.config.adverseMedia) {
    lines.push("frankieOne->amlVendor: Screen individual — Adverse media", "")
    lines.push("amlVendor-->frankieOne: Adverse media result", "")
  }
  if (!p.config.pep && !p.config.sanctions && !p.config.adverseMedia) {
    lines.push("note frankieOne: No AML screens are currently enabled for this customer", "")
  }

  lines.push("self frankieOne: Score and combine hits into an overall AML risk level", "")
  lines.push("note frankieOne: Any hit routes the case to a manual review queue for compliance sign-off", "")

  if (isV1) {
    lines.push("clientApp->frankieOne: Fetch checks summary")
    lines.push("  GET /entity/{entityId}/checks", "")
    lines.push("frankieOne-->clientApp: AML result included in the checks summary", "")
  } else {
    lines.push("frankieOne-->clientApp: AML result included in the workflow response", "")
  }

  lines.push(p.config.ongoingMonitoring
    ? "self frankieOne: Ongoing monitoring is on — re-screen periodically and raise new alerts as watchlists change"
    : "note frankieOne: Ongoing monitoring is currently off for this entity")

  return lines.join("\n")
}

// ── Fraud ──────────────────────────────────────────────────────────────────

export interface FraudDiagramParams {
  platformVersion: PlatformVersion
  signals: FraudSignalConfig[]
}

export function buildFraudDiagramDsl(p: FraudDiagramParams): string {
  const isV1 = p.platformVersion === "v1"
  const enabled = p.signals.filter((s) => s.enabled)
  const lines: string[] = []

  lines.push(`title Fraud${isV1 ? " (V1)" : ""} — Interaction Flow`, "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push(`participant frankieOne "FrankieOne" "${isV1 ? "V1 Compliance API" : "Platform"}"`)
  lines.push('participant fraudVendor "Fraud Vendor" "Sardine"', "")

  lines.push("clientApp->frankieOne: Collect device + contact signal during the onboarding session")
  lines.push('  >{"deviceSessionId":"dvc-sess-xxxx","ipAddress":"203.0.113.4","email":"applicant@example.com","phone":"+61400000000"}', "")
  lines.push(`note frankieOne: Fraud checks run as part of the same ${isV1 ? "entity verify call" : "KYC workflow execution"}`, "")

  if (enabled.length === 0) {
    lines.push("note frankieOne: No fraud signals are currently enabled for this customer", "")
  } else {
    enabled.forEach((signal) => {
      lines.push(`frankieOne->fraudVendor: ${signal.label} check`, "")
      lines.push(`fraudVendor-->frankieOne: ${signal.label} result`, "")
    })
  }

  lines.push("self frankieOne: Combine signals into an overall fraud risk level", "")

  if (isV1) {
    lines.push("clientApp->frankieOne: Fetch checks summary")
    lines.push("  GET /entity/{entityId}/checks", "")
    lines.push("frankieOne-->clientApp: Fraud result included in the checks summary")
  } else {
    lines.push("frankieOne-->clientApp: Fraud result included in the workflow response")
  }

  return lines.join("\n")
}

// ── Duplicates ─────────────────────────────────────────────────────────────

export interface DuplicatesDiagramParams {
  platformVersion: PlatformVersion
  defaultRules: DuplicateRuleConfig[]
  customRules: DuplicateRuleConfig[]
}

export function buildDuplicatesDiagramDsl(p: DuplicatesDiagramParams): string {
  const isV1 = p.platformVersion === "v1"
  const allRules = [...p.defaultRules, ...p.customRules]
  const lines: string[] = []

  lines.push(`title Duplicates${isV1 ? " (V1)" : ""} — Interaction Flow`, "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push(`participant frankieOne "FrankieOne" "${isV1 ? "V1 Compliance API" : "Platform"}"`)
  lines.push('participant entityStore "Entity Store" "Previously verified entities"', "")

  lines.push(`clientApp->frankieOne: Submit entity for verification (same request as IDV/KYC)`)
  lines.push('  >{"givenName":"...","familyName":"...","dateOfBirth":"...","emailAddress":"..."}', "")
  lines.push(`note frankieOne: Duplicate detection runs as part of the same ${isV1 ? "entity verify call" : "KYC workflow execution"}`, "")

  if (allRules.length === 0) {
    lines.push("note frankieOne: No duplicate match rules are currently configured for this customer", "")
  } else {
    lines.push(`frankieOne->entityStore: Compare against previously verified entities using ${allRules.length} configured rule${allRules.length === 1 ? "" : "s"}`)
    lines.push(`  >{"rules":[${allRules.map((r) => `"${r.name}"`).join(",")}]}`, "")
    lines.push("entityStore-->frankieOne: Matching entities (if any), with the rule + strength that matched", "")
  }

  lines.push("self frankieOne: Combine matches into a duplicate result", "")

  if (isV1) {
    lines.push("clientApp->frankieOne: Fetch checks summary")
    lines.push("  GET /entity/{entityId}/checks", "")
    lines.push("frankieOne-->clientApp: Duplicate result included in the checks summary")
  } else {
    lines.push("frankieOne-->clientApp: Duplicate result included in the workflow response")
  }

  return lines.join("\n")
}

// ── KYB (Know Your Business) ───────────────────────────────────────────────
// V1 has no organizations API — KYB is a V2-only capability today.

export interface KybDiagramParams {
  platformVersion: PlatformVersion
  workflowName: string
  auChecks: KybCheckConfig[]
  ownership: KybOwnershipConfig
}

export function buildKybDiagramDsl(p: KybDiagramParams): string {
  const enabled = p.auChecks.filter((c) => c.enabled)
  const lines: string[] = []

  lines.push("title KYB — Interaction Flow", "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push('participant frankieOne "FrankieOne" "Platform"')
  lines.push('participant registry "Business Registry" "ASIC / ABR"', "")

  if (p.platformVersion === "v1") {
    lines.push("note frankieOne: KYB runs on FrankieOne's organizations API, which is V2-only — this flow applies regardless of this customer's V1/V2 individual verification setup", "")
  }

  lines.push("clientApp->frankieOne: Look up the business in the registry")
  lines.push("  POST /organizations/lookup")
  lines.push('  >{"organizationNumber":{"registrationNumber":"052121347","registryCode":"ACN"},"region":{"country":"AU"}}', "")
  lines.push("frankieOne->registry: Search ASIC/ABR for a matching business")
  lines.push("registry-->frankieOne: Matched organisation(s) + organizationToken", "")
  lines.push("frankieOne-->clientApp: Return matched organisation(s)", "")

  lines.push("clientApp->frankieOne: Execute KYB workflow for the selected match")
  lines.push(`  POST /organizations/workflows/${p.workflowName || "{workflow}"}/execute`)
  lines.push('  >{"organizationToken":"org-tok-xxxx..."}', "")
  lines.push("self frankieOne: Create the organisation entity and start the KYB workflow", "")

  if (enabled.length === 0) {
    lines.push("note frankieOne: No Australian business checks are currently enabled for this customer", "")
  } else {
    lines.push("group KYB checks")
    enabled.forEach((check) => {
      lines.push(`  frankieOne->registry: ${check.label}`)
      lines.push("  registry-->frankieOne: Result")
    })
    lines.push("end", "")
  }

  lines.push(`self frankieOne: Resolve ownership — classify individuals as UBOs at ${p.ownership.uboThresholdPercent}%+ ownership`, "")
  lines.push(`self frankieOne: Apply blocking definitions — flag anything at/above the ${p.ownership.blockingThresholdPercent}% blocking threshold for manual review`, "")

  lines.push("frankieOne->>clientApp: Webhook — KYB workflow complete")
  lines.push("  POST https://your-server.com/webhook/frankieone", "")
  lines.push("clientApp->frankieOne: Fetch full result")
  lines.push("  GET /organizations/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions/{executionId}", "")
  lines.push("frankieOne-->clientApp: Return business verification result + resolved UBOs/officials/shareholders")

  return lines.join("\n")
}

// ── Transaction Monitoring ─────────────────────────────────────────────────
// No V1/V2 split — this stage is simulated regardless of platform version.

export function buildTmDiagramDsl(config: TmRuleConfig): string {
  const lines: string[] = []

  lines.push("title Transaction Monitoring — Interaction Flow (Simulated)", "")
  lines.push('participant clientApp "Client App" "Your product"')
  lines.push('participant frankieOne "FrankieOne" "Platform (illustrative)"')
  lines.push('participant ops "Compliance Queue" "Ops / MLRO review"', "")
  lines.push("clientApp->>frankieOne: Submit transaction event")
  lines.push('  >{"accountId":"acc-xxxx","amount":9850,"direction":"OUT","counterparty":"Silverline Holdings"}', "")
  lines.push("self frankieOne: Evaluate the transaction against configured rules", "")
  lines.push("group Rule evaluation")
  lines.push(`  self frankieOne: Reporting threshold — single transaction over AUD ${config.reportingThreshold.toLocaleString()}?`)
  lines.push(`  self frankieOne: Structuring — ${config.structuringMinCount}+ transfers just under the threshold within ${config.structuringWindowHours}h?`)
  lines.push(`  self frankieOne: Velocity — 3+ transactions totalling over AUD ${config.velocityThreshold.toLocaleString()} within ${config.velocityWindowHours}h?`)
  if (config.flagNewPayee) {
    lines.push("  self frankieOne: New payee — large transfer to a newly added payee?")
  }
  lines.push("end", "")
  lines.push("note frankieOne: A triggered rule raises an alert with a severity and rationale", "")
  lines.push("frankieOne->>ops: Alert routed to the compliance queue for review")
  lines.push("  POST https://your-server.com/webhook/frankieone", "")
  lines.push("ops-->frankieOne: Reviewer marks the alert escalated or cleared", "")
  lines.push("self frankieOne: Update case status and audit trail")

  return lines.join("\n")
}
